import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState } from "react";
import { ChevronDown, ChevronUp, Target, Dumbbell, AlertTriangle, TrendingUp } from "lucide-react";

interface ExerciseProtocol {
  id: string;
  condition: string;
  shortName: string;
  overview: string;
  goals: string[];
  categories: {
    name: string;
    examples: string[];
    dosage: string;
  }[];
  precautions: string[];
  progressionCriteria: string[];
  evidenceBase: string;
}

const PROTOCOLS: ExerciseProtocol[] = [
  {
    id: "axspa",
    condition: "Axial Spondyloarthritis (axSpA)",
    shortName: "axSpA",
    overview: "Exercise is the cornerstone of axSpA management. Daily spinal mobility, aerobic conditioning, and postural correction are all first-line interventions. BASEX (Bath AS Exercise) programme is the standard UK framework.",
    goals: [
      "Maintain and improve spinal mobility in all planes",
      "Reduce pain and stiffness via endorphin release and joint lubrication",
      "Prevent or minimise kyphotic posture",
      "Improve cardiovascular fitness and psychological wellbeing",
      "Preserve chest expansion",
    ],
    categories: [
      {
        name: "Daily Spinal Mobility (BASEX)",
        examples: ["Cervical rotation, lateral flexion, extension", "Thoracic rotation in sitting/lying", "Lumbar flexion/extension", "Hip mobility — flexion, abduction, extension", "Chest expansion exercises"],
        dosage: "Daily, 20–30 minutes. Morning is optimal to counteract overnight stiffness.",
      },
      {
        name: "Aerobic Exercise",
        examples: ["Swimming", "Cycling (upright or recumbent)", "Nordic walking", "Hydrotherapy"],
        dosage: "150 min/week moderate intensity or 75 min vigorous, per NICE PH44. Build gradually from 20 min 3×/week.",
      },
      {
        name: "Strengthening",
        examples: ["Core stabilisation", "Back extensor strengthening (prone extension)", "Hip abductor/extensor strengthening", "Scapular retraction"],
        dosage: "2–3 sessions/week, 2–3 sets of 8–15 reps. Rest day between sessions.",
      },
      {
        name: "Postural Training",
        examples: ["Wall standing correction", "Chin tuck and cervical retraction", "Breathing exercises (diaphragmatic)", "Prone lying for 20–30 min/day"],
        dosage: "Incorporate into daily routine. Prone lying daily.",
      },
    ],
    precautions: [
      "Atlantoaxial instability — uncommon in axSpA but screen with neurological symptoms",
      "Severe osteoporosis — avoid high-impact loading and spinal flexion under load",
      "Active inflammatory flare — reduce intensity; continue gentle range of movement",
      "Acute uveitis — exercise can continue; advise prompt ophthalmology review",
      "Fracture risk in advanced disease — avoid contact sports and falls-risk activities",
    ],
    progressionCriteria: [
      "No adverse symptom flare 48 hours post-exercise",
      "BASFI improvement ≥ 1 point (MCID 1.3–1.7)",
      "Able to complete current programme with RPE < 14 (Borg scale)",
      "BASMI stable or improving",
    ],
    evidenceBase: "NICE NG65 (Spondyloarthritis), BSR/BHPR axSpA guidelines, EULAR recommendations for physical activity in AS (2018), BASEX programme (Royal National Hospital for Rheumatic Diseases)",
  },
  {
    id: "ra",
    condition: "Rheumatoid Arthritis (RA)",
    shortName: "RA",
    overview: "Exercise is safe and beneficial in RA across all disease stages. Dynamic exercise improves function, cardiovascular fitness, and reduces fatigue without increasing disease activity. A combination of aerobic and resistance exercise is recommended.",
    goals: [
      "Improve muscle strength and joint stability",
      "Reduce fatigue and improve energy management",
      "Maintain or improve joint range of movement",
      "Improve cardiovascular fitness and reduce cardiometabolic risk",
      "Enhance psychological wellbeing and self-efficacy",
    ],
    categories: [
      {
        name: "Aerobic Exercise",
        examples: ["Walking", "Cycling", "Swimming/hydrotherapy", "Low-impact aerobics"],
        dosage: "150 min/week moderate intensity. Start with 10–15 min and build. Heart rate 60–75% max.",
      },
      {
        name: "Resistance/Strengthening",
        examples: ["Theraband exercises", "Free weights (hand weights)", "Bodyweight exercises", "Hydrotherapy resistance"],
        dosage: "2–3×/week. 2–3 sets, 8–12 reps. Start at 50–60% 1RM. Progress every 2–4 weeks.",
      },
      {
        name: "Range of Movement",
        examples: ["Active assisted joint ROM exercises", "Wrist/finger flexion and extension", "Shoulder circles and pendulum", "Ankle pumps and foot exercises"],
        dosage: "Daily, 10–15 minutes. Especially during flares when other exercise is reduced.",
      },
      {
        name: "Hydrotherapy",
        examples: ["Warm water walking", "Pool-based resistance exercises", "Float-assisted ROM", "Group hydrotherapy classes"],
        dosage: "1–2×/week if available. 30–45 min sessions at 35–36°C.",
      },
    ],
    precautions: [
      "Active joint inflammation — reduce loading; continue ROM and gentle aerobic activity",
      "Cervical spine instability (atlantoaxial subluxation) — screen all RA patients before cervical techniques",
      "Perioperative period — follow post-surgical rehabilitation protocols",
      "Severe joint damage — modify impact loading; consider water-based exercise",
      "Corticosteroid use — monitor for osteoporosis; emphasise weight-bearing activities",
    ],
    progressionCriteria: [
      "No joint pain lasting > 2 hours post-exercise",
      "No increase in joint swelling",
      "HAQ-DI improvement ≥ 0.22 (MCID)",
      "Patient-reported fatigue scores improving",
    ],
    evidenceBase: "NICE NG100 (RA), BSR/BHPR RA guidelines, EULAR 2018 recommendations for physical activity, Ottawa Panel guidelines for exercise in RA (2004, updated reviews)",
  },
  {
    id: "sle",
    condition: "Systemic Lupus Erythematosus (SLE)",
    shortName: "SLE",
    overview: "Exercise in SLE must be adapted to fatigue levels and disease activity. Fatigue-adapted exercise pacing using the energy envelope model is central. Low to moderate intensity aerobic exercise and strengthening are safe and reduce fatigue.",
    goals: [
      "Reduce fatigue using pacing and energy management principles",
      "Improve aerobic capacity and reduce cardiovascular risk (SLE increases CV risk significantly)",
      "Maintain muscle strength (steroid myopathy prevention)",
      "Support psychological wellbeing and reduce depression risk",
      "Improve sleep quality",
    ],
    categories: [
      {
        name: "Fatigue-Adapted Aerobic Exercise",
        examples: ["Walking (starting 5–10 min)", "Cycling (low resistance)", "Swimming", "Tai chi"],
        dosage: "Start at 50–60% VO2max, 3×/week. Use 'stop-before-you-drop' principle. Increase duration by 10% per week.",
      },
      {
        name: "Strengthening",
        examples: ["Resistance bands (light to moderate)", "Bodyweight exercises", "Aquatic strengthening"],
        dosage: "2×/week. Start with 1 set of 10 reps. Monitor for post-exertional malaise.",
      },
      {
        name: "Pacing and Energy Conservation",
        examples: ["Activity diaries", "Planned rest breaks", "Heart rate monitoring", "Symptom-guided intensity adjustment"],
        dosage: "Integrated into all activities. Review weekly. Energy envelope: stay within 70–80% of max energy budget.",
      },
    ],
    precautions: [
      "Photosensitivity — outdoor exercise in protective clothing; early morning or evening preferred",
      "Lupus nephritis — monitor blood pressure; avoid high-intensity during active nephritis",
      "Lupus carditis or pleuritis — cardiac clearance required before aerobic exercise",
      "Severe fatigue (flare) — reduce to gentle ROM and stretching only",
      "Avascular necrosis — modify weight-bearing; consider hydrotherapy",
      "Post-exertional malaise — use step-by-step graded progression with at least 24-hour recovery",
    ],
    progressionCriteria: [
      "No worsening of fatigue scores (Chalder Fatigue Scale) 24–48 hours post-exercise",
      "Stable or improving disease activity (SLEDAI not increasing)",
      "Patient-reported energy levels stable or improving",
    ],
    evidenceBase: "Carvalho et al. (2019) SLE exercise systematic review, EULAR 2019 cardiovascular recommendations, ACR/EULAR SLE management guidelines, BSR SLE guidelines",
  },
  {
    id: "myopathy",
    condition: "Inflammatory Myopathy (PM/DM/IBM)",
    shortName: "Myopathy",
    overview: "Graded exercise therapy (GET) is safe and beneficial in stable inflammatory myopathy. Historical concerns about exercise worsening inflammation have not been supported by evidence. Exercise improves strength, function, and quality of life.",
    goals: [
      "Improve proximal muscle strength (hip flexors, knee extensors, shoulder abductors)",
      "Improve functional capacity and activities of daily living",
      "Prevent or reverse deconditioning",
      "Maintain aerobic capacity",
      "Address dysphagia or respiratory muscle weakness if present (specialist referral)",
    ],
    categories: [
      {
        name: "Graded Resistance Exercise",
        examples: ["Sit-to-stand (progress to weighted)", "Step-ups", "Shoulder press (light weights)", "Seated leg press", "Theraband exercises"],
        dosage: "Start with 1 set of 10 reps at 40–50% 1RM. Progress to 3 sets over 4–6 weeks. 2–3×/week.",
      },
      {
        name: "Aerobic Conditioning",
        examples: ["Walking (supervised initially)", "Cycling", "Hydrotherapy", "Seated aerobics"],
        dosage: "Start 10–15 min, 3×/week at RPE 10–12. Build to 30 min over 8–12 weeks.",
      },
      {
        name: "Functional Training",
        examples: ["Stair climbing", "Reaching tasks", "Balance training", "ADL simulation"],
        dosage: "Integrated into sessions 2–3×/week. Progress to real-life tasks.",
      },
    ],
    precautions: [
      "Active inflammatory disease — exercise may resume once CK is stable and patient is on treatment; start very gently",
      "ILD (interstitial lung disease) — respiratory assessment before aerobic exercise; monitor SpO2",
      "Dysphagia — risk of aspiration with supine exercises; refer to SLT alongside physio",
      "Cardiac involvement — cardiac clearance required; ECG monitoring during initial sessions",
      "IBM — progressive despite treatment; realistic expectations needed; focus on function and falls prevention",
    ],
    progressionCriteria: [
      "Stable or falling CK levels (not a contraindication if stable)",
      "No significant increase in muscle pain or weakness 48 hours post-exercise",
      "MMT8 or Myositis Damage Index improving",
    ],
    evidenceBase: "Alexanderson et al. (2014) GET in myositis, Munters et al. (2013) aerobic exercise in myositis, EULAR/ACR myositis management guidelines (2019)",
  },
  {
    id: "ssc",
    condition: "Systemic Sclerosis (SSc)",
    shortName: "SSc",
    overview: "Exercise in SSc focuses on hand mobility, thoracic expansion, and maintaining functional independence. Facial and hand exercises are particularly important given microstomia and digital involvement.",
    goals: [
      "Maintain and improve hand and digit mobility",
      "Improve thoracic expansion and respiratory function",
      "Reduce skin tightness through stretching",
      "Maintain upper limb function",
      "Prevent contractures",
    ],
    categories: [
      {
        name: "Hand and Digit Exercises",
        examples: ["Full finger flexion/extension", "Thumb opposition and abduction", "Pinch and grip exercises", "Warm water hand soaking before exercise", "Intrinsic muscle stretching"],
        dosage: "2–3×/day, 10–15 minutes. After warm water soak (10 min at 37°C).",
      },
      {
        name: "Facial Exercises",
        examples: ["Jaw stretching", "Lip stretching and puckering", "Cheek stretching", "Tongue exercises"],
        dosage: "2×/day. Particularly important for microstomia. Refer to specialist SLT/OT if needed.",
      },
      {
        name: "Thoracic Mobility and Breathing",
        examples: ["Thoracic extension over foam roll", "Lateral chest stretches", "Diaphragmatic breathing", "Incentive spirometry (if ILD)"],
        dosage: "Daily. Breathing exercises 5–10 min morning and evening.",
      },
      {
        name: "Upper Limb Mobility",
        examples: ["Shoulder flexion and rotation", "Elbow extension stretching", "Wrist extension stretching", "Proprioceptive exercises"],
        dosage: "Daily ROM programme, 15–20 minutes.",
      },
    ],
    precautions: [
      "Raynaud's phenomenon — ensure warm environment; warm hands before exercise; avoid cold exposure",
      "Digital ulcers — modify hand exercises to avoid pressure on ulcer sites",
      "Pulmonary arterial hypertension (PAH) — cardiac/respiratory clearance essential; monitor SpO2",
      "ILD — aerobic exercise requires respiratory monitoring; avoid if FVC < 50%",
      "Calcinosis — protect calcified areas from pressure and trauma during exercise",
    ],
    progressionCriteria: [
      "Maintenance or improvement of hand span measurements",
      "Improved interincisal distance (mouth opening)",
      "HAQ-DI stable or improving",
      "Patient-reported hand function improving (e.g. SHAQ)",
    ],
    evidenceBase: "EULAR SSc management recommendations (2017), Rannou et al. (2007) hand rehabilitation in SSc, CSP guidance on respiratory physiotherapy in ILD",
  },
];

function ProtocolCard({ protocol }: { protocol: ExerciseProtocol }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
      >
        <div>
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">{protocol.shortName}</span>
          <h3 className="text-lg font-bold text-slate-900">{protocol.condition}</h3>
          <p className="text-sm text-slate-500 mt-1">{protocol.overview.slice(0, 100)}…</p>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0 ml-4" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 ml-4" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-6 space-y-6">
          <p className="text-sm text-slate-700 leading-relaxed">{protocol.overview}</p>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-emerald-600" />
              <h4 className="font-semibold text-slate-900">Treatment Goals</h4>
            </div>
            <ul className="space-y-1.5">
              {protocol.goals.map((g, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 mt-2" />
                  {g}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Dumbbell className="h-4 w-4 text-emerald-600" />
              <h4 className="font-semibold text-slate-900">Exercise Categories & Dosage</h4>
            </div>
            <div className="space-y-4">
              {protocol.categories.map((cat, i) => (
                <div key={i} className="bg-emerald-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-slate-800 text-sm">{cat.name}</h5>
                    <span className="text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full font-medium">{cat.dosage}</span>
                  </div>
                  <ul className="space-y-1">
                    {cat.examples.map((ex, j) => (
                      <li key={j} className="text-xs text-slate-600 flex items-start gap-2">
                        <span className="text-emerald-400 shrink-0">→</span> {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h4 className="font-semibold text-slate-900">Precautions</h4>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <ul className="space-y-1.5">
                {protocol.precautions.map((p, i) => (
                  <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 mt-2" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <h4 className="font-semibold text-slate-900">Progression Criteria</h4>
            </div>
            <ul className="space-y-1.5">
              {protocol.progressionCriteria.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-emerald-500 font-bold shrink-0">✓</span>
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Evidence Base</p>
            <p className="text-xs text-slate-600">{protocol.evidenceBase}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExerciseProtocols() {
  const [filter, setFilter] = useState("all");

  const conditions = ["all", ...PROTOCOLS.map((p) => p.id)];
  const filtered = filter === "all" ? PROTOCOLS : PROTOCOLS.filter((p) => p.id === filter);

  return (
    <PhysioLayout title="Exercise & Rehab Protocols">
      <div className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <h2 className="font-bold text-slate-900 mb-1">Evidence-Based Exercise Prescription</h2>
          <p className="text-sm text-slate-600">Condition-specific rehabilitation protocols for rheumatology physiotherapy practice. Each protocol includes goals, exercise categories with dosage, precautions, and progression criteria grounded in UK and international guidelines.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {conditions.map((c) => {
            const label = c === "all" ? "All Conditions" : PROTOCOLS.find((p) => p.id === c)?.shortName ?? c;
            return (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === c
                    ? "bg-emerald-700 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-700"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="space-y-4">
          {filtered.map((protocol) => (
            <ProtocolCard key={protocol.id} protocol={protocol} />
          ))}
        </div>
      </div>
    </PhysioLayout>
  );
}
